import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { COMMUNICATION_EVENTS, COMMUNICATION_RESULT_STATUSES } from "models";
import { COMMUNICATION_LOG_TAB } from "modules/AppointmentDetails/components/CommunicationLog";
import { Agreements, CheckinResults, CommunicationEvents, Services } from "modules/AppointmentDetails/components/CommunicationLog/components";

import type { Appointment, CommunicationAgreement, CommunicationEvent } from "models";

type TabContentType = {
  id: number;
  tab: COMMUNICATION_LOG_TAB;
  appointment: Appointment;
  onSelectAgreement?: (agreement: CommunicationAgreement) => void;
};

const onlineCheckinEventTypes = [COMMUNICATION_EVENTS.OnlineCheckInSent, COMMUNICATION_EVENTS.OnlineCheckInOpened, COMMUNICATION_EVENTS.OnlineCheckInAnswered];
const repairOverviewEventTypes = [COMMUNICATION_EVENTS.RepairOverviewSent, COMMUNICATION_EVENTS.RepairOverviewResent, COMMUNICATION_EVENTS.RepairOverviewOpened];
const diagnoseOverviewEventTypes = [
  COMMUNICATION_EVENTS.DiagnoseOverviewSent,
  COMMUNICATION_EVENTS.DiagnoseOverviewOpened,
  COMMUNICATION_EVENTS.DiagnoseOverviewResent,
  COMMUNICATION_EVENTS.DiagnoseOverviewAnswered
];

const getAgreementsAndEvents = (id: number, tab: COMMUNICATION_LOG_TAB, appointment: Appointment) => {
  let events: CommunicationEvent[] = [];

  if (tab === COMMUNICATION_LOG_TAB.OnlineCheckin) {
    events = appointment.customer_communication?.events?.filter(e => onlineCheckinEventTypes.includes(e.type)) || [];
  } else if (tab === COMMUNICATION_LOG_TAB.RepairOverview) {
    events = appointment.customer_communication?.events?.filter(e => repairOverviewEventTypes.includes(e.type)) || [];
  } else {
    const diagnoseEvents = appointment.customer_communication?.events?.filter(e => diagnoseOverviewEventTypes.includes(e.type)) || [];

    let eventIndex = diagnoseEvents.findIndex(e => e.id === id);

    if (eventIndex > -1) {
      do {
        events.push(diagnoseEvents[eventIndex]);
        eventIndex += 1;
      } while (eventIndex < diagnoseEvents.length && diagnoseEvents[eventIndex].type !== COMMUNICATION_EVENTS.DiagnoseOverviewResent);
    }
  }

  const agreements = events.find(e => e.agreements)?.agreements || [];

  return { events, agreements };
};

export const TabContent = ({ tab, id, appointment, onSelectAgreement }: TabContentType) => {
  const { t } = useTranslation();

  const { events, agreements } = useMemo(() => getAgreementsAndEvents(id, tab, appointment), [tab]);

  const agreedInterventions = events
    .find(e => [COMMUNICATION_EVENTS.OnlineCheckInOpened, COMMUNICATION_EVENTS.DiagnoseOverviewOpened].includes(e.type))
    ?.intervention_results?.filter(i => i.communication_result_status === COMMUNICATION_RESULT_STATUSES.OkStatus);

  const declinedInterventions = events
    .find(e => COMMUNICATION_EVENTS.DiagnoseOverviewOpened === e.type)
    ?.intervention_results?.filter(i => i.communication_result_status === COMMUNICATION_RESULT_STATUSES.NotOkStatus);

  const contactInterventions = events
    .find(e => COMMUNICATION_EVENTS.DiagnoseOverviewOpened === e.type)
    ?.intervention_results?.filter(i => i.communication_result_status === COMMUNICATION_RESULT_STATUSES.ContactMeStatus);

  const agreedReplacements = events
    .find(e => COMMUNICATION_EVENTS.DiagnoseOverviewOpened === e.type)
    ?.replacement_results?.filter(i => i.communication_result_status === COMMUNICATION_RESULT_STATUSES.OkStatus);

  const declinedReplacements = events
    .find(e => COMMUNICATION_EVENTS.DiagnoseOverviewOpened === e.type)
    ?.replacement_results?.filter(i => i.communication_result_status === COMMUNICATION_RESULT_STATUSES.NotOkStatus);

  const contactReplacements = events
    .find(e => COMMUNICATION_EVENTS.DiagnoseOverviewOpened === e.type)
    ?.replacement_results?.filter(i => i.communication_result_status === COMMUNICATION_RESULT_STATUSES.ContactMeStatus);

  return (
    <>
      <CommunicationEvents events={events} />
      <Services results={agreedInterventions} title={t("v8_accepted_scheduled_services", "Accepted scheduled services")} />
      <Services results={declinedInterventions} title={t("v8_declined_scheduled_services", "Declined scheduled services")} />
      <Services results={contactInterventions} title={t("v8_contact_scheduled_services", "Contact scheduled services")} />
      <Services results={agreedReplacements} title={t("v8_accepted_tyre_replacement", "Accepted tyre replacement")} />
      <Services results={declinedReplacements} title={t("v8_declined_tyre_replacement", "Declined tyre replacement")} />
      <Services results={contactReplacements} title={t("v8_contact_tyre_replacement", "Contact tyre replacement")} />
      {appointment.customer_communication?.check_in_results && tab === COMMUNICATION_LOG_TAB.OnlineCheckin && (
        <CheckinResults results={appointment.customer_communication.check_in_results} />
      )}
      <Agreements agreements={agreements} onSelectAgreement={onSelectAgreement} />
    </>
  );
};
